import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type PartnerStatus } from '@/api/routes/partners/partners.api';
import { type PartnerEventDetail } from '@/api/routes/partners/partners.events.api';
import { IconCircleCheck } from '@/components/icon/tabler/IconCircleCheck';
import { IconCircleX } from '@/components/icon/tabler/IconCircleX';
import { EVENT_SUBMIT_REQUIREMENTS, getEventSubmitChecklist } from '@/domain/events/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  event: PartnerEventDetail;
  partnerStatus: PartnerStatus;
  onReviewAddress: () => void;
}

/** P10 side column: what is missing before sending. An unlocated address blocks the submit. */
const EventSubmitChecklist = ({ event, partnerStatus, onReviewAddress }: Props) => {
  const t = useTranslate('domain.events.components.EventSubmitChecklist');
  const checklist = getEventSubmitChecklist(event, partnerStatus);

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('title')}
      </Typography>
      <Stack component="ul" gap={1.5} sx={sx.list}>
        {EVENT_SUBMIT_REQUIREMENTS.map((requirement) => {
          const met = checklist[requirement];
          return (
            <Stack component="li" key={requirement} direction="row" gap={1.25}>
              {met ? (
                <IconCircleCheck color="success" fontSize="small" />
              ) : (
                <IconCircleX color="error" fontSize="small" />
              )}
              <Stack gap={0.25}>
                <Typography variant="body2" fontWeight={500}>
                  {t(`${requirement}.${met ? 'met' : 'missing'}`)}
                </Typography>
                {met ? null : (
                  <Typography variant="caption" color="text.secondary">
                    {t(`${requirement}.hint`)}
                  </Typography>
                )}
                {!met && requirement === 'addressLocated' ? (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={onReviewAddress}
                  >
                    {t('reviewAddress')}
                  </Link>
                ) : null}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default EventSubmitChecklist;

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  list: { m: 0, p: 0, listStyle: 'none' },
};
